import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';

import { useAgentChats, useChatMessages, useSendMessageV2, useStartAgentChat } from '@codes/chatbots';
import { useWebCoreStore } from '@codes/web-core';

import { useChatBase } from './useChatBase';

import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

interface UseAgentChatProps {
    agentId: string;
}

export const useAgentChat = ({ agentId }: UseAgentChatProps) => {
    const { t } = useTranslation();
    const { profile } = useWebCoreStore();

    // State management
    const [currentChat, setCurrentChat] = useState<ChatView | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const initializedRef = useRef(false);

    // Data fetching
    const { data: agentChatsData, isLoading: chatsLoading } = useAgentChats({ view: 'mine', stereo: 'root', agentId });
    const { data: messagesData, isLoading: isMessagesLoading } = useChatMessages({
        rootId: currentChat?.id,
        limit: -1,
    });

    const startMyChat = useStartAgentChat();
    const sendMessage = useSendMessageV2();

    // Shared chat base logic
    const {
        displayMessages,
        input,
        setInput,
        isWaitingResponse,
        clearPendingMessage,
        createPendingMessage,
        markPendingAsError,
        updateChatMessages,
    } = useChatBase({ chatId: currentChat?.id, messagesData, limit: -1 });

    // Initialize chat
    const initializeChat = useCallback(async () => {
        if (!currentChat && profile && agentId && !initializedRef.current && !chatsLoading) {
            try {
                initializedRef.current = true;
                setIsLoading(true);

                if (agentChatsData?.data && agentChatsData.data.length > 0) {
                    setCurrentChat(agentChatsData.data[0]);
                } else {
                    const chatName = t('agent.chat.defaultName', { agentId });
                    const profile$ = {
                        ...(profile?.sid && { sid: profile.sid }),
                        ...(profile?.uid && { uid: profile.uid }),
                        ...(profile?.$user?.gender && { gender: profile?.$user?.gender }),
                        ...(profile?.$user?.name && { name: profile?.$user?.name }),
                    };

                    const newChat = await startMyChat.mutateAsync({ name: chatName, profile$, agentId });
                    setCurrentChat(newChat);
                }
            } catch (error) {
                console.error('Failed to start chat:', error);
                toast({ title: t('chat.create_new_chat_failed') });
                initializedRef.current = false;
            } finally {
                setIsLoading(false);
            }
        }
    }, [profile, agentId, startMyChat, t, agentChatsData?.data, chatsLoading, currentChat]);

    useEffect(() => {
        initializeChat();
    }, [initializeChat]);

    // Message handling
    const addMessage = useCallback(
        async (messageContent: string) => {
            const pending = createPendingMessage(messageContent);
            if (!pending || !currentChat?.id) {
                return;
            }

            try {
                const res = await sendMessage.mutateAsync({
                    groupId: currentChat.id!,
                    body: {
                        ...(profile?.sid && { sid: profile.sid }),
                        content: messageContent.trim(),
                    },
                });

                await updateChatMessages(currentChat.id, res.list || []);
                clearPendingMessage();
            } catch (error) {
                console.error('Failed to send message:', error);
                markPendingAsError();
                toast({ title: t('chat.send_message_failed') });
            }
        },
        [
            currentChat,
            sendMessage,
            profile,
            clearPendingMessage,
            createPendingMessage,
            markPendingAsError,
            updateChatMessages,
            t,
        ]
    );

    return {
        currentChat,
        displayMessages,
        input,
        setInput,
        addMessage,
        isLoading,
        isWaitingResponse,
        isMessagesLoading,
    };
};
